import { AssetSubClassCode } from '@api/api/entity-constants';
import { Entity } from '@api/api/entity-schema';
import { RangeHorizon } from '@chart';
import { ErrorBoundaryWrapper } from '@terminal/components/error-boundary-wrapper/ErrorBoundaryWrapper';
import { isEconomicSeries } from '@terminal/components/price-display-view/helpers/price-display-helpers';
import { BrandAndProductDrawer } from '@terminal/modules/brand-and-product-drawer/BrandAndProductDrawer';
import { ChartLoading } from '@terminal/modules/simple-chart/components/chart-loading/ChartLoading';
import {
  isCountryOrArea,
  useCountryGdp,
} from '@terminal/react-query-hooks/country-gdp/useCountryGdp';
import { useCompanyCompetitors } from '@terminal/react-query-hooks/knowledge-graph/useKnowledgeGraph';
import {
  isLongTail,
  LEGACY_AO_DRAWER_SUB_CLASSES,
  useLongTailSeries,
} from '@terminal/react-query-hooks/long-tail-series/useLongTailSeries';
import { POSTHOG_TRACKING_SOURCES } from '@terminal/services/tracking/posthog/constants';
import { useFlags } from '@terminal/stores/use-flags/useFlags';
import { useSubscription } from '@terminal/stores/use-subscription/useSubscription';
import { NewsView } from '@terminal/views/news/components/news-view/NewsView';
import { ThemeEarningsDrawer } from '@terminal/views/theme-overview/tab-content/theme-earnings/ThemeEarningsDrawer';
import { useThemeEarningsSummaryDrawer } from '@terminal/views/theme-overview/tab-content/theme-earnings/useThemeEarningsSummaryDrawer';
import { AssetFinancialsWidget } from '@terminal/widgets/asset-financials-widget/AssetFinancialsWidget';
import { ChartPeerGroup } from '@terminal/widgets/chart-peer-group/ChartPeerGroup';
import { Document } from '@terminal/widgets/document-widget/DocumentWidget';
import { Earnings } from '@terminal/widgets/earnings/Earnings';
import { CompetitorsDrawer } from '@terminal/widgets/knowledge-graph-widget/components/competitors-tab/competitors-drawer/CompetitorsDrawer';
import { GeographicExposureDrawer } from '@terminal/widgets/knowledge-graph-widget/components/geographic-exposure-tab/drawers/GeographicExposureDrawer';
import { KnowledgeGraphWidget } from '@terminal/widgets/knowledge-graph-widget/KnowledgeGraphWidget';
import { LegacyOverview } from '@terminal/widgets/overview/LegacyOverview';
import { Overview, OverviewChart } from '@terminal/widgets/overview/Overview';
import { PEER_GROUP_DISPLAYED } from '@terminal/widgets/overview/utils';
import { ThemeDrawer } from '@terminal/widgets/theme-widget/theme-drawer/ThemeDrawer';
import { useEffect, useEffectEvent } from 'react';
import { TabsType } from './types';
import { getTabs } from './utils';

export interface AssetOverviewTabsProps {
  entity: Entity;
  activeTab: TabsType;
  isSeriesLoading?: boolean;
  documentsCount?: number;
  isDocumentsLoading?: boolean;
  onTabFallback?: (tab: TabsType) => void;
}

const TabContentContent = ({
  entity,
  activeTab,
  isSeriesLoading = false,
  documentsCount = 0,
  isDocumentsLoading = false,
  onTabFallback,
}: AssetOverviewTabsProps) => {
  const { drawerProps: themeEarningsDrawerProps } =
    useThemeEarningsSummaryDrawer();
  const isAoPulseEnabled = useFlags(state =>
    state.getFeatureFlagState('PLATFORM-5199-ao-pulse')
  );
  const isStock = entity.sub_class === AssetSubClassCode.EquityStock;
  const isNewEarningsTabEnabled = useFlags(state =>
    state.getFeatureFlagState('PLATFORM-4671-ao-earnings')
  );
  const tabs = getTabs({
    options: {
      isStock,
      isChartTabVisible:
        isSeriesLoading || isStock || !!entity.default_snake.trim(),
      isDocumentsTabVisible:
        (isStock || entity.sub_class === AssetSubClassCode.EcoCentralBank) &&
        (isDocumentsLoading || documentsCount > 0),
    },
    isNewEarningsTabEnabled,
  });
  const applicableTab = tabs.some(
    tab => tab.tabKey === activeTab && !tab.isHidden
  )
    ? activeTab
    : TabsType.Overview;
  const handleTabFallback = useEffectEvent((tab: TabsType) => {
    onTabFallback?.(tab);
  });
  useEffect(() => {
    if (applicableTab !== activeTab) {
      handleTabFallback(applicableTab);
    }
  }, [activeTab, applicableTab]);
  const isBasicUser = useSubscription(state => state.isBasicUser());
  const { competitorsData, isFetching } = useCompanyCompetitors({
    entityTag: entity.tag,
    limit: PEER_GROUP_DISPLAYED,
    enabled: isStock,
  });
  const peerEntities = competitorsData.map(s => s.entity);

  const showPulseOverview =
    isAoPulseEnabled &&
    !isBasicUser &&
    !LEGACY_AO_DRAWER_SUB_CLASSES.has(entity.sub_class);

  const renderElement = () => {
    switch (applicableTab) {
      case TabsType.Financials: {
        return <AssetFinancialsWidget entity={entity} />;
      }

      case TabsType.Chart: {
        if (isSeriesLoading) {
          return <ChartLoading isLegacyVariant={false} isChatMessage={false} />;
        }
        if (isLongTail(entity)) {
          return (
            <OverviewChart
              entity={entity}
              initialHorizon={
                isEconomicSeries(entity)
                  ? RangeHorizon.OneYear
                  : RangeHorizon.ThreeMonths
              }
            />
          );
        }
        return (
          <ChartPeerGroup
            entity={entity}
            peerEntities={peerEntities}
            isFetching={isFetching}
          />
        );
      }

      case TabsType.Connections: {
        return <KnowledgeGraphWidget entity={entity} />;
      }

      case TabsType.NewsInsights: {
        return (
          <NewsView
            entity={entity}
            includedFilterKeys={['providers']}
            trackingSource={POSTHOG_TRACKING_SOURCES.ASSET_OVERVIEW}
          />
        );
      }

      case TabsType.Documents: {
        return (
          <ErrorBoundaryWrapper showBorder={false}>
            <Document
              entityTag={entity.tag}
              entitySubClass={entity.sub_class}
            />
          </ErrorBoundaryWrapper>
        );
      }

      case TabsType.Earnings: {
        return <Earnings entity={entity} />;
      }

      case TabsType.Overview:

      default: {
        return showPulseOverview ? (
          <Overview
            entity={entity}
            key={entity.tag}
            isSeriesLoading={isSeriesLoading}
          />
        ) : (
          <LegacyOverview
            entity={entity}
            key={entity.tag}
            isSeriesLoading={isSeriesLoading}
          />
        );
      }
    }
  };

  return (
    <>
      {isStock && (
        <>
          <CompetitorsDrawer entity={entity} />
          <ThemeDrawer entity={entity} />
          <GeographicExposureDrawer entity={entity} />
          <BrandAndProductDrawer entity={entity} />
        </>
      )}
      <ThemeEarningsDrawer {...themeEarningsDrawerProps} />
      {renderElement()}
    </>
  );
};

const CountryTabContent = (props: AssetOverviewTabsProps) => {
  const resolved = useCountryGdp(props.entity);
  return <TabContentContent {...props} {...resolved} />;
};

const LongTailTabContent = (props: AssetOverviewTabsProps) => {
  const resolved = useLongTailSeries(props.entity);
  return <TabContentContent {...props} {...resolved} />;
};

export const TabContent = (props: AssetOverviewTabsProps) =>
  isCountryOrArea(props.entity) ? (
    <CountryTabContent {...props} />
  ) : isLongTail(props.entity) ? (
    <LongTailTabContent {...props} />
  ) : (
    <TabContentContent {...props} />
  );
